import { useState } from 'react'

import { Input } from '@/components/ui/Input'
import { venueCampaignImageReference, uploadVenueImage, VenueImageUploadError } from '@/lib/venue/venueImageUploadApi'

function FieldError({ message }: { message: string | undefined }) {
  if (!message) return null
  return <p className="mt-1 text-xs text-danger-500">{message}</p>
}

/**
 * 지역 홍보 작성 — 이미지 첨부(선택) + 링크 입력(요구사항 "제목·내용·이미지·링크 입력").
 *
 * 🚨 이미지 업로드는 기존 `uploadVenueImage`(구장 사진 업로드 파이프라인)를 그대로 재사용하되
 * `venueCampaignImageReference`로 스토리지 경로만 분리한다(설계 §자율 결정 8). 새 업로드 파이프라인을
 * 만들지 않는다.
 */
export function PromotionMediaFields({
  venueId,
  imageUrl,
  linkUrl,
  linkUrlError,
  onChangeImageUrl,
  onChangeLinkUrl,
}: {
  venueId: string
  imageUrl: string | null
  linkUrl: string
  linkUrlError: string | undefined
  onChangeImageUrl: (imageUrl: string | null) => void
  onChangeLinkUrl: (linkUrl: string) => void
}) {
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)

  async function handleFileSelected(file: File | null) {
    if (!file) return
    setUploading(true)
    setUploadError(null)
    try {
      const result = await uploadVenueImage(file, venueId, 0, venueCampaignImageReference(venueId))
      onChangeImageUrl(result.imageUrl)
    } catch (error) {
      const message = error instanceof VenueImageUploadError ? error.message : '이미지를 업로드하지 못했습니다.'
      setUploadError(message)
    } finally {
      setUploading(false)
    }
  }

  return (
    <div className="space-y-4">
      <div>
        <label className="mb-1.5 block text-sm font-medium text-ink-700" htmlFor="promotion-image-input">
          이미지(선택)
        </label>
        {imageUrl ? (
          <div className="mb-2 flex items-center gap-3">
            <img src={imageUrl} alt="홍보 이미지" className="h-20 w-20 rounded-lg border border-slateish-200 object-cover" />
            <button
              type="button"
              className="text-xs text-danger-500 underline"
              onClick={() => {
                onChangeImageUrl(null)
              }}
            >
              제거
            </button>
          </div>
        ) : null}
        <input
          id="promotion-image-input"
          type="file"
          accept="image/*"
          disabled={uploading}
          className="block text-sm text-ink-700"
          onChange={(event) => {
            void handleFileSelected(event.target.files?.[0] ?? null)
            event.target.value = ''
          }}
        />
        {uploading ? <p className="mt-1 text-xs text-slateish-500">업로드 중…</p> : null}
        {uploadError ? <FieldError message={uploadError} /> : null}
      </div>

      <div>
        <label className="mb-1.5 block text-sm font-medium text-ink-700" htmlFor="promotion-link-url">
          링크(선택)
        </label>
        <Input
          id="promotion-link-url"
          value={linkUrl}
          aria-invalid={Boolean(linkUrlError)}
          placeholder="https://…"
          onChange={(event) => {
            onChangeLinkUrl(event.target.value)
          }}
        />
        <FieldError message={linkUrlError} />
      </div>
    </div>
  )
}
